import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, initMapFactory, initMultiMapFactory, queryMapFactory, readonlyHookFactory } from "../common/reducers/utilities";
import moment from "moment-timezone";
import { ensureArray } from "../common/utils/arrayUtilities";
import { parseDate } from "../common/utils/dateUtils";
import { createEagerSelector, eagerMetaFactory } from "../common/reducers/pendingUpdateSlice";
import { aggregatingFetch } from "../common/hooks/networkHook";
import { writeField } from "../common/utils/objectUtilities";
import { complexTableQuery } from "../common/utils/tableUtilities";

let processPCR = (pcr) => {
    const result = { ...pcr };
    if (result.displayid) {
        result.displayid = parseInt(result.displayid);
    }
    if (result.dob) {
        result.age = moment().diff(result.dob, 'years', false);
    }
    return result;
}

const safeFields = ['id','firstname','initialpatientacuity','finalpatientacuity','problem','lastname','dob','gender','displayid','eventid','creation','finalized','finalization_username','exported','medications_given', 'has_allergies','jobs'];

const makeSimplePCR = epcr => safeFields.reduce((simple, field) => { simple[field] = epcr[field]; return simple; }, {});

const epcrSlice = createSlice({
    name: 'epcr',
    initialState: {
        simple: [],
        full: [],
    },
    reducers: {
        addOrUpdateSimple: (state, action) => {
            for (const epcr of ensureArray(action.payload).map(processPCR)) {
                let idx = state.simple.findIndex(e => e.id === epcr.id);
                if (idx >= 0) {
                    state.simple.splice(idx, 1, epcr);
                } else {
                    state.simple.push(epcr);
                }
            }
        },
        addOrUpdateFull: (state, action) => {
            for (const epcr of ensureArray(action.payload).map(processPCR)) {
                let idx = state.full.findIndex(e => e.id === epcr.id);
                if (idx >= 0) {
                    state.full.splice(idx, 1, epcr);
                } else {
                    state.full.push(epcr);
                }
                const simpleEpcr = makeSimplePCR(epcr);
                idx = state.simple.findIndex(e => e.id === simpleEpcr.id);
                if (idx >= 0) {
                    state.simple.splice(idx, 1, simpleEpcr);
                } else {
                    state.simple.push(simpleEpcr);
                }
            }
        },
        removeFull: (state, action) => {
            for (const id of ensureArray(action.payload).map(epcr => parseInt(epcr.id))) {
                const idx = state.full.findIndex(e => e.id === id);
                if (idx > -1) {
                    state.full.splice(idx, 1);
                }
            }
        },
        removeSimple: (state, action) => {
            for (const id of ensureArray(action.payload).map(epcr => parseInt(epcr.id))) {
                const idx = state.simple.findIndex(e => e.id === id);
                if (idx > -1) {
                    state.full.splice(idx, 1);
                }
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => epcrSlice.getInitialState());
    }
    
});

// #region network operations
const eagerMeta = eagerMetaFactory(epcrSlice.name);
const fetchIndependentEpcrs = (network) => network.get('/api/v1/independentPCRs', epcrSlice.actions.addOrUpdateSimple);
const fetchJobEpcrs = aggregatingFetch(
    jobid => network => network.get('/api/v1/jobPCRs/' + encodeURIComponent(jobid), epcrSlice.actions.addOrUpdateSimple),
    jobids => network => network.post('/api/v1/jobPCRs', jobids, epcrSlice.actions.addOrUpdateSimple)
);

const fetchFullEpcr = id => network => network.get('/api/v2/epcr/' + encodeURIComponent(id), epcrSlice.actions.addOrUpdateFull);
const fetchHistoricEpcrs = (creationAfter, creationBefore) => {
    return network => {
        return network.post('/api/v2/epcrHistory', { creationAfter, creationBefore }, epcrSlice.actions.addOrUpdateSimple);
    }
}
export const fetchReferencedEpcrs = (field, value) => network => network.get(`/api/v2/epcrListRef/${field}/${value}`, epcrSlice.actions.addOrUpdateSimple);
export const updateEpcr = (network, delta) => network.put('/api/v2/epcr', delta, epcrSlice.actions.addOrUpdateFull, eagerMeta(delta.id)); // TODO should we actually do something on failure?
export const createEpcr = async (network, epcr) => {
    epcr.id = epcr.id ?? crypto.randomUUID();
    const res = await network.post('/api/v2/epcr', epcr, epcrSlice.addOrUpdateFull, eagerMeta(epcr.id)); // TODO should we actually do something on failure?
    processPCR(res);
    return res; 
}
export const connectEpcr = (websocket, id) => websocket.emit('connectPCRv2', {id});
export const disconnectEpcr = (websocket, id) => websocket.emit('disconnectPCR');
// #endregion

// #region selectors
// When a create or update happens the changes are copied into both the full and simple arrays. 
// Therefore we can use one set of pending updates and apply it to either the full or simple arrays depending on the request.
// The pending updates may contain fields not in the list of 'safeFields' held by the simple epcrs, but these fields won't be displayed,
// and the user would be allowed to view them anyway - they're the ones who made the update.
const eagerSelector = createEagerSelector(epcrSlice.name, { mode: 'array' });
const selectFullEpcrsBase = state => state.epcr.full;
const selectFullEpcrs = eagerSelector(selectFullEpcrsBase);
const selectSimpleEpcrsBase = state => state.epcr.simple;
const selectSimpleEpcrs = eagerSelector(selectSimpleEpcrsBase);

const selectFullEpcrByID = createSelector([selectFullEpcrs, (epcrs, id) => id], (epcrs, id) => epcrs.find(e => e.id == id));
const selectFullEpcr = id => state => selectFullEpcrByID(state, id); 

const selectEpcrsByJobIDs = createSelector([selectSimpleEpcrs, (epcrs, jobids) => jobids], (epcrs, jobids) => {
    const toFind = new Set(ensureArray(jobids).map(jobid => jobid?.toString()));
    return epcrs.filter(e => e.jobs?.some(jobid => toFind.has(jobid?.toString())));
});
const selectJobEpcrs = jobids => state => selectEpcrsByJobIDs(state, jobids);

const selectEpcrsByNoJobID = createSelector([selectSimpleEpcrs], epcrs => epcrs.filter(e => !e.jobs || e.jobs?.length === 0))
const selectIndependentEpcrs = state => selectEpcrsByNoJobID(state);

const selectEpcrSearchResults = (creationAfter, creationBefore) => createSelector([selectSimpleEpcrs], pcrs => {
    return pcrs.filter(e => {
        return (!creationAfter || (parseDate(e.creation) > parseDate(creationAfter))) && (!creationBefore || (parseDate(e.creation) < parseDate(creationBefore)));
    })
})

const selectReferencedEpcrs = (field, value) => createSelector([selectSimpleEpcrs], pcrs => {
    const query = {};
    writeField(query, field, value);
    return complexTableQuery(pcrs, query)
});

export const peekEpcrs = search => network => network.post('/api/v2/epcrSearch', search);

// #endregion

// #region listeners
const listen = {
    epcrCreatedv2: (epcr, { dispatch }) => {
        if (Object.keys(epcr).some(f => !safeFields.includes(f))) {
            dispatch(epcrSlice.actions.addOrUpdateFull(epcr))
        }
        dispatch(epcrSlice.actions.addOrUpdateSimple(makeSimplePCR(epcr)))
    },
    epcrUpdatedv2: (epcr, { dispatch }) => {
        if (Object.keys(epcr).some(f => !safeFields.includes(f))) {
            dispatch(epcrSlice.actions.addOrUpdateFull(epcr))
        }
        dispatch(epcrSlice.actions.addOrUpdateSimple(makeSimplePCR(epcr)))
    }
}
// #endregion
const simplePCRCleanup = { cleanup: { root: 'epcr', all: selectSimpleEpcrs, removeAll: epcrSlice.actions.removeSimple } };
const initFullEpcr = initMapFactory(fetchFullEpcr, listen);
const initJobEpcrs = initMapFactory(fetchJobEpcrs, listen);
export const [useEpcr, realizeEpcr] = dataHookFactory(selectFullEpcr, initFullEpcr);
export const [useReadonlyEpcr] = readonlyHookFactory(selectFullEpcr, initFullEpcr);
export const [useJobEpcrs] = dataHookFactory(selectJobEpcrs, initJobEpcrs, simplePCRCleanup);
export const [useJobsEpcrs] = dataHookFactory(selectJobEpcrs, initMultiMapFactory(initJobEpcrs, listen), simplePCRCleanup);
export const [useIndependentEpcrs] = dataHookFactory(selectIndependentEpcrs, { run: fetchIndependentEpcrs, listen }, simplePCRCleanup);
export const [useEpcrHistory] = dataHookFactory(selectEpcrSearchResults, initMapFactory(fetchHistoricEpcrs , listen), simplePCRCleanup);
const initReferencedEpcrs = initMapFactory(fetchReferencedEpcrs, listen);
export const [useReferencedEpcrs] = dataHookFactory(selectReferencedEpcrs, initReferencedEpcrs, simplePCRCleanup); 

const reducerDetails = {name: epcrSlice.name, reducer: epcrSlice.reducer };
export default reducerDetails;